import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { expensesAPI, lotesAPI, proveedoresAPI } from '../lib/api';
import { CATEGORIAS, SUBCATEGORIAS_ALIMENTO, FASES, UNIDADES, getCategoriasByFase } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { CurrencyDollar, ArrowLeft, Building, PencilSimple } from '@phosphor-icons/react';
import { Skeleton } from '../components/ui/skeleton';

export default function ExpenseEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [lotes, setLotes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [superuserCode, setSuperuserCode] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [formData, setFormData] = useState({
    lote_id: '',
    fase: '',
    categoria: '',
    subcategoria: '',
    factura: '',
    descripcion: '',
    cantidad: '',
    unidad: 'QQS',
    precio_unitario: '',
    proveedor_id: '',
    fecha: new Date().toISOString().split('T')[0],
    moneda: 'DOP',
    estado_pago: 'pagado',
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [lotesRes, proveedoresRes, expenseRes] = await Promise.all([
        lotesAPI.getAll(),
        proveedoresAPI.getAll(),
        expensesAPI.getOne(id)
      ]);
      
      setLotes(lotesRes.data);
      setProveedores(proveedoresRes.data);
      
      const expense = expenseRes.data;
      
      // Extract factura number from description if present
      let factura = expense.factura_numero || '';
      let descripcion = expense.descripcion || '';
      
      // Try to extract factura from descripcion if not in factura_numero
      if (!factura && descripcion.includes('Fact #')) {
        const match = descripcion.match(/Fact #(\S+)/);
        if (match) {
          factura = match[1];
          // Remove factura part from descripcion for medicamentos
          if (expense.categoria === 'medicamentos') {
            descripcion = descripcion.replace(/Fact #\S+\s*-?\s*/, '').trim();
          }
        }
      }
      
      setFormData({
        lote_id: expense.lote_id || '',
        fase: expense.fase || '',
        categoria: expense.categoria || '',
        subcategoria: expense.subcategoria || '',
        factura: factura,
        descripcion: descripcion,
        cantidad: expense.cantidad?.toString() || '',
        unidad: expense.unidad || 'QQS',
        precio_unitario: expense.precio_unitario?.toString() || '',
        proveedor_id: expense.proveedor_id || '',
        fecha: expense.fecha ? expense.fecha.split('T')[0] : new Date().toISOString().split('T')[0],
        moneda: expense.moneda || 'DOP',
        estado_pago: expense.estado_pago || 'pagado',
      });
      
      // Marcar que ya se cargaron los datos iniciales
      setTimeout(() => setIsInitialLoad(false), 100);
    } catch (error) {
      toast.error('Error al cargar el gasto');
      navigate('/gastos');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Solo resetear categoría si el usuario cambia la fase manualmente (no durante carga inicial)
    if (name === 'fase' && !isInitialLoad) {
      setFormData((prev) => ({ ...prev, categoria: '', subcategoria: '' }));
    }
    
    if (name === 'categoria' && value !== 'alimento') {
      setFormData((prev) => ({ ...prev, subcategoria: '' }));
    }
    
    if (name === 'categoria' && !isInitialLoad) {
      if (value === 'alimento') {
        setFormData((prev) => ({ ...prev, unidad: 'QQS', moneda: 'USD' }));
      } else if (value === 'medicamentos') {
        setFormData((prev) => ({ ...prev, unidad: 'ml', moneda: 'DOP' }));
      } else if (['mano_obra_vacuna', 'mano_obra_amarradores', 'nomina', 'gasto_amarradores', 'incentivo', 'alquiler', 'gastos_financieros', 'cena_venta'].includes(value)) {
        setFormData((prev) => ({ ...prev, unidad: 'servicio', moneda: 'DOP' }));
      } else if (value === 'vacunas') {
        setFormData((prev) => ({ ...prev, unidad: 'dosis', moneda: 'DOP' }));
      } else if (value === 'gasolina') {
        setFormData((prev) => ({ ...prev, unidad: 'galon', moneda: 'DOP' }));
      }
    }
  };
  
  const categoriasDisponibles = formData.fase ? getCategoriasByFase(formData.fase) : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!superuserCode) {
      toast.error('Ingrese el código de super usuario');
      return;
    }
    
    const needsFactura = ['alimento', 'medicamentos'].includes(formData.categoria);
    
    if (!formData.lote_id || !formData.fase || !formData.categoria || !formData.cantidad || !formData.precio_unitario) {
      toast.error('Por favor complete todos los campos obligatorios');
      return;
    }
    
    if (needsFactura && !formData.factura) {
      toast.error('Por favor ingrese el número de factura');
      return;
    }
    
    if (formData.categoria === 'alimento' && !formData.subcategoria) {
      toast.error('Por favor seleccione el tipo de alimento');
      return;
    }

    setLoading(true);
    try {
      let descripcionFinal = '';
      if (formData.categoria === 'alimento') {
        descripcionFinal = `Fact #${formData.factura}`;
      } else if (formData.categoria === 'medicamentos') {
        descripcionFinal = `Fact #${formData.factura} - ${formData.descripcion}`;
      } else {
        descripcionFinal = formData.descripcion || formData.categoria;
      }
      
      const payload = {
        lote_id: formData.lote_id,
        fase: formData.fase,
        categoria: formData.categoria,
        subcategoria: formData.subcategoria || null,
        descripcion: descripcionFinal,
        cantidad: parseFloat(formData.cantidad),
        unidad: formData.unidad,
        precio_unitario: parseFloat(formData.precio_unitario),
        proveedor: null,
        proveedor_id: formData.proveedor_id || null,
        fecha: formData.fecha || null,  // Enviar como YYYY-MM-DD sin conversión a UTC
        moneda: formData.moneda,
        factura_numero: formData.factura || null,
        estado_pago: formData.estado_pago,
      };
      
      await expensesAPI.update(id, payload, superuserCode);
      toast.success('Gasto actualizado exitosamente');
      navigate('/gastos');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Código de super usuario incorrecto');
      } else {
        const message = error.response?.data?.detail || 'Error al actualizar el gasto';
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const total = formData.cantidad && formData.precio_unitario
    ? (parseFloat(formData.cantidad) * parseFloat(formData.precio_unitario)).toFixed(2)
    : '0.00';

  const selectedLote = lotes.find((l) => l.id === formData.lote_id);
  const tasaCambio = selectedLote?.tasa_cambio || 61.5;
  const totalNum = parseFloat(total) || 0;
  const totalDOP = formData.moneda === 'USD' ? totalNum * tasaCambio : totalNum;
  const totalUSD = formData.moneda === 'USD' ? totalNum : totalNum / tasaCambio;

  if (loadingData) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto space-y-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-[600px]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto animate-fade-in" data-testid="edit-expense-form">
        <div className="mb-6">
          <Link
            to="/gastos"
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
          >
            <ArrowLeft size={14} />
            Volver a Gastos
          </Link>
          <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Editar Gasto
          </h1>
          <p className="text-muted-foreground">
            Modifique los datos del gasto
          </p>
        </div>

        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PencilSimple size={24} className="text-blue-600" weight="fill" />
              Información del Gasto
            </CardTitle>
            <CardDescription>
              Edite los datos del gasto seleccionado
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Lote Selection */}
              <div className="space-y-2">
                <Label htmlFor="lote_id">Lote *</Label>
                <Select
                  value={formData.lote_id}
                  onValueChange={(value) => handleChange('lote_id', value)}
                >
                  <SelectTrigger data-testid="select-lote">
                    <SelectValue placeholder="Seleccione un lote" />
                  </SelectTrigger>
                  <SelectContent>
                    {lotes.map((lote) => (
                      <SelectItem key={lote.id} value={lote.id}>
                        {lote.nombre} - {lote.estado === 'activo' ? `Día ${lote.dias_transcurridos}` : 'Cerrado'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Phase Selection */}
              <div className="space-y-2">
                <Label htmlFor="fase">Fase *</Label>
                <Select
                  value={formData.fase}
                  onValueChange={(value) => handleChange('fase', value)}
                >
                  <SelectTrigger data-testid="select-fase">
                    <SelectValue placeholder="Seleccione la fase" />
                  </SelectTrigger>
                  <SelectContent>
                    {FASES.map((fase) => (
                      <SelectItem key={fase.value} value={fase.value}>
                        <div>
                          <span className="font-medium">{fase.label}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            ({fase.description})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Category Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="categoria">Categoría *</Label>
                  <Select
                    value={formData.categoria}
                    onValueChange={(value) => handleChange('categoria', value)}
                    disabled={!formData.fase}
                  >
                    <SelectTrigger data-testid="select-categoria">
                      <SelectValue placeholder={formData.fase ? "Seleccione categoría" : "Seleccione fase primero"} />
                    </SelectTrigger>
                    <SelectContent>
                      {categoriasDisponibles.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.categoria === 'alimento' && (
                  <div className="space-y-2">
                    <Label htmlFor="subcategoria">Tipo de Alimento *</Label>
                    <Select
                      value={formData.subcategoria}
                      onValueChange={(value) => handleChange('subcategoria', value)}
                    >
                      <SelectTrigger data-testid="select-subcategoria">
                        <SelectValue placeholder="Seleccione tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {SUBCATEGORIAS_ALIMENTO.map((sub) => (
                          <SelectItem key={sub.value} value={sub.value}>
                            {sub.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Helper text for fase */}
              {formData.fase && (
                <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                  {FASES.find(f => f.value === formData.fase)?.description}
                </p>
              )}

              {/* Factura and Description - varies by category */}
              {formData.categoria === 'alimento' && (
                <div className="space-y-2">
                  <Label htmlFor="factura">Fact # *</Label>
                  <Input
                    id="factura"
                    placeholder="Número de factura"
                    value={formData.factura}
                    onChange={(e) => handleChange('factura', e.target.value)}
                    className="input-focus"
                    data-testid="expense-factura"
                  />
                </div>
              )}

              {formData.categoria === 'medicamentos' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="factura">Fact # *</Label>
                    <Input
                      id="factura"
                      placeholder="Número de factura"
                      value={formData.factura}
                      onChange={(e) => handleChange('factura', e.target.value)}
                      className="input-focus"
                      data-testid="expense-factura"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="descripcion">Descripción *</Label>
                    <Input
                      id="descripcion"
                      placeholder="Ej: VITAMINA A"
                      value={formData.descripcion}
                      onChange={(e) => handleChange('descripcion', e.target.value)}
                      className="input-focus"
                      data-testid="expense-descripcion"
                    />
                  </div>
                </div>
              )}

              {formData.categoria && !['alimento', 'medicamentos'].includes(formData.categoria) && (
                <div className="space-y-2">
                  <Label htmlFor="descripcion">Descripción</Label>
                  <Textarea
                    id="descripcion"
                    placeholder="Descripción del gasto"
                    value={formData.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    className="input-focus resize-none"
                    rows={2}
                    data-testid="expense-descripcion"
                  />
                </div>
              )}

              {/* Quantity and Price */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cantidad">Cantidad *</Label>
                  <Input
                    id="cantidad"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="Ej: 100"
                    value={formData.cantidad}
                    onChange={(e) => handleChange('cantidad', e.target.value)}
                    className="input-focus"
                    data-testid="expense-cantidad"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="unidad">Unidad</Label>
                  <Select
                    value={formData.unidad}
                    onValueChange={(value) => handleChange('unidad', value)}
                  >
                    <SelectTrigger data-testid="select-unidad">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIDADES.map((u) => (
                        <SelectItem key={u.value} value={u.value}>
                          {u.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="precio_unitario">Precio Unitario *</Label>
                  <Input
                    id="precio_unitario"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="Ej: 0.50"
                    value={formData.precio_unitario}
                    onChange={(e) => handleChange('precio_unitario', e.target.value)}
                    className="input-focus"
                    data-testid="expense-precio"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="moneda">Moneda</Label>
                  <Select
                    value={formData.moneda}
                    onValueChange={(value) => handleChange('moneda', value)}
                  >
                    <SelectTrigger data-testid="select-moneda">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="DOP">DOP (RD$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Provider, Payment Status and Date */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="proveedor_id" className="flex items-center gap-2">
                    <Building size={14} />
                    Proveedor
                  </Label>
                  <Select
                    value={formData.proveedor_id || "none"}
                    onValueChange={(value) => handleChange('proveedor_id', value === "none" ? "" : value)}
                  >
                    <SelectTrigger data-testid="select-proveedor">
                      <SelectValue placeholder="Seleccionar proveedor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin proveedor</SelectItem>
                      {proveedores.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.nombre} ({p.moneda_habitual})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estado_pago">Estado de Pago</Label>
                  <Select
                    value={formData.estado_pago}
                    onValueChange={(value) => handleChange('estado_pago', value)}
                  >
                    <SelectTrigger data-testid="select-estado-pago">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pagado">Pagado (Contado)</SelectItem>
                      <SelectItem value="credito">A Crédito</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fecha">Fecha</Label>
                  <Input
                    id="fecha"
                    type="date"
                    value={formData.fecha}
                    onChange={(e) => handleChange('fecha', e.target.value)}
                    className="input-focus"
                    data-testid="expense-fecha"
                  />
                </div>
              </div>

              {/* Total Preview */}
              <div className="bg-muted/50 rounded-lg p-4 border border-border/60">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total del Gasto</p>
                    <p className="text-xs text-muted-foreground">
                      {formData.cantidad || 0} {formData.unidad} × {formData.moneda === 'USD' ? '$' : 'RD$'}{formData.precio_unitario || '0.00'}
                    </p>
                    {selectedLote && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Tasa: 1 USD = {tasaCambio} DOP
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#F69031]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      RD$ {totalDOP.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      ≈ US$ {totalUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Superuser Code */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-amber-700 text-sm mb-3">
                  <CurrencyDollar size={18} />
                  <span className="font-medium">Acción protegida - Requiere código de super usuario</span>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="superuser-code">Código de Super Usuario *</Label>
                  <Input
                    id="superuser-code"
                    type="password"
                    value={superuserCode}
                    onChange={(e) => setSuperuserCode(e.target.value)}
                    placeholder="Ingrese el código..."
                    className="input-focus"
                    data-testid="superuser-code"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/gastos')}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 btn-animate"
                  disabled={loading || !superuserCode}
                  data-testid="submit-expense"
                >
                  {loading ? (
                    <div className="spinner h-5 w-5" />
                  ) : (
                    <>
                      <PencilSimple size={18} className="mr-2" />
                      Guardar Cambios
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
